import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Send, MapPin, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useWilayas, useCommunesByWilaya } from "@/hooks/useAlgeriaData";
import { loadDraft, clearDraft, useDraftAutosave, type DraftAnnonceData } from "@/hooks/useDraftAnnonce";
import { ANNONCE_CATEGORIES, ANNONCE_URGENCY } from "@/lib/constants";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { useSEO } from "@/hooks/useSEO";

const categories = ANNONCE_CATEGORIES;

const PosterAnnonce = () => {
  const { locale, t } = useLanguage("annonces");
  const lNavigate = useLocalizedNavigate();
  useSEO({
    title: locale === "ar" ? "انشر طلبك — حرافي" : "Poster une annonce — Hirafi",
    description: locale === "ar"
      ? "انشر طلب خدمتك مجانًا واحصل على عروض من حرفيين موثّقين في جميع ولايات الجزائر."
      : "Publiez votre demande de travaux gratuitement et recevez des propositions d'artisans qualifiés dans toutes les wilayas d'Algérie.",
    canonical: "/poster-annonce",
    locale,
  });
  const navigate = useNavigate(); // pour navigate(-1)
  const allWilayasData = useWilayas(locale);
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [addressHint, setAddressHint] = useState("");
  const [description, setDescription] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  // Communes for current wilaya — city_name (FR) as value for DB compatibility
  const communeOptions = useCommunesByWilaya(wilaya, locale);

  // Localisation du profil (pour pré-remplissage)
  const [profileWilaya, setProfileWilaya] = useState("");
  const [profileCommune, setProfileCommune] = useState("");
  const profileApplied = useRef(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      setTitle(draft.title || "");
      setCategory(draft.category || "");
      setUrgency(draft.urgency || "");
      setWilaya(draft.wilaya || "");
      setCommune(draft.commune || "");
      setAddressHint(draft.addressHint || "");
      setDescription(draft.description || "");
      setBudgetMin(draft.budgetMin || "");
      setBudgetMax(draft.budgetMax || "");
    }
  }, []);

  // Charge la localisation du profil quand l'utilisateur est connecté
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("wilaya, commune")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setProfileWilaya(data.wilaya || "");
          setProfileCommune(data.commune || "");
        }
      });
  }, [user]);

  // Pré-remplit wilaya/commune depuis le profil si le formulaire est encore vide
  useEffect(() => {
    if (profileApplied.current || !profileWilaya) return;
    profileApplied.current = true;
    setWilaya((prev) => prev || profileWilaya);
    setCommune((prev) => prev || profileCommune);
  }, [profileWilaya, profileCommune]);

  // Redirige les visiteurs non connectés vers la page de connexion
  useEffect(() => {
    if (!authLoading && !user) {
      lNavigate("/connexion?redirect=/poster-annonce", { replace: true });
    }
  }, [user, authLoading]);

  const publishingRef = useRef(false);

  // Autosave draft to localStorage
  const draftData: DraftAnnonceData = useMemo(() => ({
    title, category, urgency, wilaya, commune, addressHint, description, budgetMin, budgetMax,
  }), [title, category, urgency, wilaya, commune, addressHint, description, budgetMin, budgetMax]);
  useDraftAutosave(draftData);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPhotoFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = useCallback(async (userId: string): Promise<string[]> => {
    if (photoFiles.length === 0) return [];
    const photoUrls: string[] = [];
    const uploadedPaths: string[] = [];
    try {
      for (const file of photoFiles) {
        const ext = file.name.split(".").pop();
        const path = `annonces/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("annonces").upload(path, file);
        if (uploadErr) throw uploadErr;
        uploadedPaths.push(path);
        const { data: urlData } = supabase.storage.from("annonces").getPublicUrl(path);
        photoUrls.push(urlData.publicUrl);
      }
    } catch (err) {
      if (uploadedPaths.length > 0) {
        await supabase.storage.from("annonces").remove(uploadedPaths);
      }
      const message = err instanceof Error ? err.message : t("form.error");
      toast.error(t("form.photo_upload_error"), { description: message });
      throw err;
    }
    return photoUrls;
  }, [photoFiles, t]);

  const publishAnnonce = useCallback(async (asDraft = false) => {
    if (publishingRef.current) return;

    const currentUser = (await supabase.auth.getUser()).data.user;
    if (!currentUser) {
      toast.error(t("form.auth_required"));
      return;
    }

    publishingRef.current = true;
    setLoading(true);
    try {
      const photoUrls = await uploadPhotos(currentUser.id);

      const { error } = await supabase.from("annonces").insert({
        user_id: currentUser.id,
        title,
        category,
        metier: ANNONCE_CATEGORIES.find((c) => c.value === category)?.label || category,
        type_travaux: "moyen",
        urgency,
        delai: urgency,
        wilaya,
        commune,
        address_hint: addressHint || null,
        description: description || null,
        budget_min: budgetMin ? parseInt(budgetMin) : null,
        budget_max: budgetMax ? parseInt(budgetMax) : null,
        budget: budgetMin && budgetMax ? `${budgetMin} - ${budgetMax} DA` : budgetMin ? `${budgetMin} DA` : null,
        photos: photoUrls,
        status: asDraft ? "draft" : "published",
      });

      if (error) throw error;

      clearDraft();
      if (asDraft) {
        toast.success(t("form.draft_saved"), { description: t("form.draft_saved_desc") });
      } else {
        toast.success(t("form.published"), { description: t("form.published_desc") });
      }
      lNavigate(asDraft ? "/mes-annonces" : "/annonces");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("form.error");
      toast.error(t("form.error"), { description: message });
    } finally {
      publishingRef.current = false;
      setLoading(false);
    }
  }, [title, category, urgency, wilaya, commune, addressHint, description, budgetMin, budgetMax, uploadPhotos, lNavigate, t]);

  const validateForm = (_asDraft: boolean): boolean => {
    if (!title || !category || !urgency || !wilaya || !commune) {
      toast.error(t("form.required_fields"), { description: t("form.required_fields_desc") });
      return false;
    }
    if (!ANNONCE_CATEGORIES.some((c) => c.value === category)) {
      toast.error(t("form.category_unavailable"), { description: t("form.category_unavailable_desc") });
      return false;
    }
    if (budgetMin && parseInt(budgetMin) < 0) {
      toast.error(t("form.budget_invalid"), { description: t("form.budget_negative") });
      return false;
    }
    if (budgetMax && parseInt(budgetMax) < 0) {
      toast.error(t("form.budget_invalid"), { description: t("form.budget_max_negative") });
      return false;
    }
    if (budgetMin && budgetMax && parseInt(budgetMax) < parseInt(budgetMin)) {
      toast.error(t("form.budget_invalid"), { description: t("form.budget_max_less") });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent, asDraft = false) => {
    e.preventDefault();
    if (!validateForm(asDraft)) return;
    await publishAnnonce(asDraft);
  };

  if (authLoading) return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold">{t("poster.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("poster.subtitle")}</p>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6 rounded-xl border bg-card p-6">
          {/* Titre */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_title")} *</Label>
            <Input placeholder={t("form.title_placeholder")} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Catégorie */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_category")} *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t("form.category_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {t(`categories_all.${c.value}`, { defaultValue: c.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Urgence */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_urgency")} *</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger>
                <SelectValue placeholder={t("form.urgency_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {ANNONCE_URGENCY.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {t(`urgency.${u.value}`, { defaultValue: u.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Localisation */}
          <div className="space-y-2">
            {user && profileWilaya && wilaya === profileWilaya && (
              <p className="flex items-center gap-1 text-xs text-primary/80">
                <MapPin className="h-3 w-3" /> {t("form.prefilled")}
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="font-display font-semibold">{t("form.label_wilaya")} *</Label>
                <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setCommune(""); }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("form.label_wilaya")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allWilayasData
                      .sort((a, b) => a.label.localeCompare(b.label, locale === "ar" ? "ar" : "fr"))
                      .map((w) => (
                        <SelectItem key={w.state_name} value={w.state_name}>{w.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-display font-semibold">{t("form.label_commune")} *</Label>
                <Select value={commune} onValueChange={setCommune}>
                  <SelectTrigger><SelectValue placeholder={t("form.label_commune")} /></SelectTrigger>
                  <SelectContent>
                    {communeOptions.map((c) => (
                      <SelectItem key={c.city_name} value={c.city_name}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Indice d'adresse */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_address_hint")}</Label>
            <Input placeholder={t("form.address_hint_placeholder")} value={addressHint} onChange={(e) => setAddressHint(e.target.value)} />
            <p className="text-xs text-muted-foreground">{t("form.address_hint_help")}</p>
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_budget")}</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input type="number" min="0" placeholder="Min" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} />
              <Input type="number" min="0" placeholder="Max" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_description")}</Label>
            <Textarea
              placeholder={t("form.description_placeholder")}
              className="min-h-[120px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">
              {t("form.label_photos")} <span className="text-muted-foreground font-normal">({t("common.optional")})</span>
            </Label>
            <label
              htmlFor="photo-upload"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 text-center transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              <Camera className="h-8 w-8 text-primary" />
              <p className="text-sm text-muted-foreground">{t("form.photo_click")}</p>
            </label>
            <input id="photo-upload" type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoChange} />
            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {photoPreviews.map((src, i) => (
                  <div key={i} className="relative group">
                    <img src={src} alt={`Preview ${i}`} className="aspect-square rounded-lg object-cover border" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 end-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={loading}
              onClick={() => navigate(-1)}
            >
              {t("common.cancel")}
            </Button>
            <Button variant="hero" size="xl" className="flex-1" type="submit" disabled={loading}>
              <Send className="me-2 h-5 w-5" />
              {loading ? t("form.publishing") : t("form.publish")}
            </Button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default PosterAnnonce;
